import type { ExtractPagingResponse, PagingResponse } from './type'

import { isPlainObject, mapTree } from '@vben/utils'

import { merge } from 'lodash-es'

type ApiGenerator<T extends string, R extends Recordable> = (ctx: {
  mapResponse: <TResponse, R>(
    res: TResponse,
    mapFn: (item: ExtractPagingResponse<TResponse>) => R,
  ) => TResponse extends {
    page?: any
  }
    ? PagingResponse<R>
    : TResponse extends any[]
      ? R[]
      : R
  prefix: <const P extends string>(path: P) => `${T}/${P}`
}) => R

export function defineApi<const T extends string, R extends Recordable>(
  prefix: T,
  apiGenerator: ApiGenerator<T, R>,
) {
  function mapResponse(res: any, mapFn: (item: Recordable) => any) {
    try {
      if (isPaging(res)) {
        const { list = [] } = res
        res.list = mapTree(list, mapFn)
      } else if (Array.isArray(res)) {
        res = res.map((item) => mapFn(item))
      } else {
        res = mapFn(res)
      }
      return res
    } catch (error) {
      console.error(error)
      return res
    }
  }

  return apiGenerator({
    prefix: (path) => (path ? `${prefix}/${path}` : prefix) as any,
    mapResponse: mapResponse as any,
  })
}

defineApi.config = {
  pagingFields: {
    total: 'total',
    page: 'page',
    pageSize: 'pageSize',
    list: 'list',
  },
}

defineApi.setConfig = (config: typeof defineApi.config) => {
  defineApi.config = merge(defineApi.config, config)
}

export function isPaging(data: unknown): data is PagingResponse<any> {
  if (isPlainObject(data)) {
    const { list, total } = defineApi.config.pagingFields
    return list in data && total in data
  }
  return false
}

export async function pagingData<T>(
  data: T[],
  params: {
    page: number
    pageSize: number
  },
  delay = 0,
) {
  if (delay) {
    await new Promise((resolve) => setTimeout(resolve, delay))
  }
  return {
    list: data.slice(
      (params.page - 1) * params.pageSize,
      params.page * params.pageSize,
    ),
    ...params,
    total: data.length,
  }
}
